import { Chip } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatBytes } from '../../utils/files';
import Loadable from '../common/loadable';

type UsageStatsProps = {
    isLoading: boolean;
    totalSize: number;
    totalRecordings: number;
    quota?: number;
};

const Container = styled(Box)`
    display: flex;
    gap: 16px;
`;

type ProgressChipWrapperProps = {
    usagePercentage: number;
};

const ProgressChipWrapper = styled(Box)<ProgressChipWrapperProps>`
    position: relative;
    display: inline-block;
    .chip-progress-bar {
        position: absolute;
        top: 0;
        left: -4px;
        height: 100%;
        background-color: ${({ theme }) => theme.colors.success};
        opacity: 0.75;
        z-index: 1;
        width: ${({ usagePercentage }) => `${usagePercentage}%`};
    }
    .chip-progress-warn {
        .chip-progress-bar {
            background-color: ${({ theme }) => theme.colors.warning} !important;
        }
    }
    .chip-progress-error {
        color: ${({ theme }) => theme.colors.white};
        .chip-progress-bar {
            background-color: ${({ theme }) => theme.colors.error} !important;
        }
    }
    .MuiChip-root {
        position: relative;
        overflow: hidden;
        .MuiChip-label {
            z-index: 2;
        }
    }
`;

export const UsageStats = (props: UsageStatsProps) => {
    const { isLoading, totalSize, totalRecordings, quota } = props;
    let usagePercentage = quota && quota > 0 ? (totalSize / quota) * 100 : 0;
    usagePercentage = usagePercentage < 100 ? usagePercentage : 100;
    const { t } = useTranslation('admin_settings');
    const chipClasses = ['chip-progress'];
    if (usagePercentage >= 80 && usagePercentage < 100) {
        chipClasses.push('chip-progress-warn');
    } else if (usagePercentage === 100) {
        chipClasses.push('chip-progress-error');
    }
    return (
        <Loadable isLoading={isLoading}>
            <Container>
                <ProgressChipWrapper usagePercentage={usagePercentage}>
                    {quota && quota > 0 ? (
                        <Chip
                            className={chipClasses.join(' ')}
                            // Note - using avatar for the progress meter.
                            avatar={<Box className="chip-progress-bar"></Box>}
                            label={t('recordings.storageUsedExpanded', {
                                used: formatBytes(totalSize),
                                quota: formatBytes(quota)
                            })}
                        />
                    ) : (
                        <Chip label={t('recordings.storageUsed', { used: formatBytes(totalSize) })} />
                    )}
                </ProgressChipWrapper>
                <Chip label={t('recordings.totalRecordings', { total: totalRecordings })} />
            </Container>
        </Loadable>
    );
};
